import { useAmp } from "next/amp"
import classNames from "classnames"
import parse, { convertNodeToElement } from "react-html-parser"
// @ts-ignore
import sha1 from "js-sha1"
import styled from "styled-components"
import { observer } from "mobx-react"
import { useStore } from "@features/common"
import { AmpImage } from "@lib/amp"
import {
  CommonProps,
  commonProps2css,
  applyCommonProps,
  unsetCommonProps,
} from "styles/helpers"
import * as Sentry from "@sentry/nextjs"

import { RichContent } from "./rich-content"
import { HtmlEmbedded } from "./html-embedded"
import { ArticleSlider } from "./slider"
import { AuthorImage } from "./author-image"
import { Images } from "./images"
import { GapBanner } from "./gap-banner"
import {
  CAROUSEL_CLASS,
  IMAGES_CLASS,
  AUTHOR_IMAGE_CLASS,
  BANNER_CLASS,
  BANNER_IMAGE_CLASS,
  GAP_BANNER_CLASS,
  GAP_BANNER_IMAGE_CLASS,
  GAP_BANNER_CONTENT_CLASS,
  GAP_BANNER_BUTTON_CLASS,
  GAP_BANNER_TITLE_CLASS,
  VIDEO_CLASS,
  VIDEO_ELEMENT_CLASS,
} from "./consts"

import type { CSSProperties } from "react"

const htmlParser = require("htmlparser2")

interface BlogContentProps extends CommonProps {
  className?: string
  style?: CSSProperties
  title?: string
  src?: string
}

export const RichContentView = observer(function RichContentView(
  props: BlogContentProps,
) {
  const { className, style, title, src, ...otherProps } = props
  const { GlobalModel, ResponsiveModel } = useStore()

  const isAmp = useAmp()

  if (!src) return null

  const transformHtml = () => {
    let counter = 0
    return parse(src, {
      decodeEntities: true,
      // @ts-ignore
      transform: (node, index: number) => {
        const { name = "", attribs = {}, children = [] } = node

        if (name === "figure" && attribs?.class?.startsWith("image")) {
          const {
            attribs: {
              src = "",
              width = 1,
              height = 1,
              alt = `${title}. ${++counter}`,
            },
          } = children[0]

          if (!ResponsiveModel.isWebPSupported && src.endsWith(".webp")) {
            Sentry.captureMessage(
              `Article "${title}" content: WebP image isn't supported`,
            )
          }

          return isAmp ? (
            <div className="mb-8" key={index}>
              <AmpImage
                src={src}
                width={(width / height).toFixed(2).toString()}
              />
            </div>
          ) : (
            <div className={attribs.class} key={index}>
              <img src={src} alt={alt} />
            </div>
          )
        }

        if (name === "div" && attribs?.class === "raw-html-embed") {
          const source = htmlParser.DomUtils.getOuterHTML(children, {
            xmlMode: true,
          })
          const uniqId = sha1.create()
          uniqId.update(source)
          uniqId.hex()

          return (
            <HtmlEmbedded
              key={index}
              uniqId={uniqId}
              source={source}
              htmlEmbeddedStore={GlobalModel.htmlEmbeddedList}
              setHtmlEmbeddedStore={GlobalModel.setHtmlEmbedded}
            />
          )
        }

        if (attribs?.class === CAROUSEL_CLASS) {
          const images = children.map((child) => ({
            src: child?.attribs?.["data-source"] ?? "",
            alt: `${title}.${counter++}`,
          }))
          return <ArticleSlider images={images} key={index} />
        }

        if (attribs?.class === BANNER_CLASS) {
          const image = children.find(
            // @ts-ignore
            (child) => child.attribs.class === BANNER_IMAGE_CLASS,
          )
          const { "data-source": imgSrc } = image.attribs
          const href = "https://pureapp.onelink.me/M5p9/4djnpjgl"

          const imgDOMNode = `<div class="${BANNER_IMAGE_CLASS}">
                <img src="${imgSrc}" alt="" />
              </div>`

          const content = children.find(
            // @ts-ignore
            (child) => child.attribs.class === "editor-banner-content",
          )

          return (
            <a
              key={index}
              className={BANNER_CLASS}
              href={href}
              dangerouslySetInnerHTML={{
                __html:
                  imgDOMNode +
                  htmlParser.DomUtils.getOuterHTML(content, {
                    xmlMode: true,
                  }),
              }}
              rel="noreferrer"
            />
          )
        }

        if (attribs?.class?.startsWith(GAP_BANNER_CLASS)) {
          const image = children.find(
            (child) => child.attribs.class === GAP_BANNER_IMAGE_CLASS,
          )
          const { "data-source": imgSrc } = image.attribs
          const href = "https://pureapp.onelink.me/M5p9/4djnpjgl"
          const hrefAppGallery = "https://pureapp.onelink.me/M5p9/uaqkx9xz"

          const content = children.find(
            (child) => child.attribs.class === GAP_BANNER_CONTENT_CLASS,
          )
          const title = content.children.find(
            (child) => child.attribs.class === GAP_BANNER_TITLE_CLASS,
          )
          const button = content.children.find(
            (child) => child.attribs.class === GAP_BANNER_BUTTON_CLASS,
          )
          button.attribs = {}

          return (
            <GapBanner
              key={index}
              imageSrc={imgSrc}
              className={attribs.class}
              href={href}
              hrefAppGallery={hrefAppGallery}
              text={convertNodeToElement(title, index, () => {})}
              button={convertNodeToElement(button, index, () => {})}
            />
          )
        }

        if (name === "a") {
          return (
            <a
              key={index}
              href={attribs?.href}
              target="_blank"
              dangerouslySetInnerHTML={{
                __html: htmlParser.DomUtils.getOuterHTML(children, {
                  xmlMode: true,
                }),
              }}
              rel="noreferrer"
            />
          )
        }

        if (attribs?.class === AUTHOR_IMAGE_CLASS) {
          const source = attribs["data-source"]
          return <AuthorImage key={index} source={source} />
        }

        if (attribs?.class === IMAGES_CLASS) {
          const images = children.map(
            // @ts-ignore
            (child) => ({
              src: child?.attribs?.["data-source"] ?? "",
              alt: `${title}.${counter++}`,
            }),
          )
          return (
            <Images
              key={index}
              images={images}
              align={attribs["data-block-align"] ?? "left"}
              blockBorder={attribs["data-block-border"] ?? "none"}
              blockRadius={attribs["data-block-radius"] ?? "none"}
            />
          )
        }

        if (attribs?.class === VIDEO_CLASS) {
          const video = children.find(
            (child) => child?.attribs?.class === VIDEO_ELEMENT_CLASS,
          )

          return (
            <video autoPlay muted loop playsInline>
              <source
                src={video?.attribs?.["data-src"]}
                type={video?.attribs?.["data-type"]}
              />
            </video>
          )
        }
      },
    })
  }

  return (
    <Root
      className={classNames("ck-content", className)}
      style={style}
      {...otherProps}
      {...applyCommonProps(otherProps)}
    >
      {transformHtml()}
    </Root>
  )
})

const Root = styled((props) => {
  const resolveProps = { ...props }
  unsetCommonProps(resolveProps)
  return <RichContent {...resolveProps} />
})`
  ${(props) => commonProps2css(props)}

  & blockquote {
    & > p {
      padding-bottom: 8px;
    }
  }
`
